import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { Checkbox, Collapse } from '@mui/material';
import { Plus } from 'lucide-react';

import Button from '../../common/Button';
import { useGetDistinguishModel } from '../../hooks/tanstack/useUtilsHook';

type CheckboxesCollapseProps = {
  name: string;
  title: string;
  items: {
    label: string;
    value: number;
    type: 'default' | 'user';
  }[];
  disabled?: boolean;
  category: string;
  handleAddExpense?: ({ category }: { category: string }) => void;
};

const CheckboxesCollapse = ({
  title,
  name,
  items,
  category,
  disabled,
  handleAddExpense,
}: CheckboxesCollapseProps) => {
  const [open, setOpen] = useState(true);
  const [checkedAll, setCheckAll] = useState(false);

  const { model } = useGetDistinguishModel();

  const { register, setValue, watch } = useFormContext();

  const data = Object.assign(watch());

  const isIndeterminate = useMemo(() => {
    const userData = data['user']?.[name] ?? {};
    const defaultData = data['default']?.[name] ?? {};

    const userChecked = Object.keys(userData).some((key) => userData[key]);

    const defaultChecked = Object.keys(defaultData).some(
      (key) => defaultData[key]
    );

    return !checkedAll && (userChecked || defaultChecked);
  }, [data, name]);

  const handleToggle = () => {
    setOpen(!open);
  };

  useEffect(() => {
    const preDefinedExpenses = model?.predefined_expenses ?? [];
    const userDefinedExpense = model?.userdefined_expenses ?? [];

    items?.forEach((item) => {
      const type = item.type;

      const expensesByGroup =
        type === 'default' ? preDefinedExpenses : userDefinedExpense;

      const isChecked = expensesByGroup.includes(Number(item.value));

      setValue(`${item.type}.${name}.${item.value}`, isChecked);
    });
  }, [
    model?.predefined_expenses,
    model?.userdefined_expenses,
    name,
    items.length,
  ]);

  useEffect(() => {
    const isCheckedAll = items.every(
      (item) => data?.[item.type]?.[name]?.[item.value]
    );

    setCheckAll(isCheckedAll);
  }, [data, items]);

  const handleToggleAll = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();

    const isChecked = !isIndeterminate && (e.target as any).checked;

    setCheckAll(isChecked);

    items.forEach((item) => {
      setValue(`${item.type}.${name}.${item.value}`, isChecked);
    });
  };

  return (
    <div className="px-4">
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center gap-1 text-[#333]"
      >
        {open ? <ArrowDropUp /> : <ArrowDropDown />}
        <Checkbox
          checked={checkedAll}
          disabled={disabled}
          onClick={disabled ? undefined : handleToggleAll}
          indeterminate={isIndeterminate}
        />
        <label className="text-lg font-bold capitalize">{title}</label>
      </button>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <div className="border-gray-300 mb-4 ml-12 flex flex-col items-start border-l pl-3">
          {data &&
            items.map((item) => {
              const id = `${item.type}.${name}.${item.value}`;

              const isChecked =
                data[item.type]?.[name]?.[`${item.value}`] || false;

              return (
                <div key={id} className="flex justify-between">
                  <div className="flex items-center gap-1">
                    <Checkbox
                      id={id}
                      disabled={disabled}
                      checked={isChecked}
                      {...register(id)}
                    />
                    <label htmlFor={id} className="flex gap-2 items-center">
                      <p>{item.label}</p>
                      {item.type === 'user' && (
                        <span className="rounded-md font-bold bg-[#F5F5F5] text-[#666666] px-[10px] py-1 text-sm">
                          Custom
                        </span>
                      )}
                    </label>
                  </div>
                </div>
              );
            })}
          {handleAddExpense && !disabled && (
            <Button
              onClick={() => (disabled ? null : handleAddExpense({ category }))}
              variant={disabled ? 'link_disabled' : 'link_red'}
              type="button"
            >
              <Plus size={15} />
              <p>Add</p>
            </Button>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default CheckboxesCollapse;
